import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { c } from '../layout/AppShell';
import { PLANS, planLabel } from '../../config/plans';
import { track } from '../../utils/analytics';

// Copy per ADR-025 resource. Everything you already received stays yours —
// a cap only blocks NEW cost-bearing work (§2.2). Purchase language only.
const RESOURCE_COPY = {
  property_check: {
    title: 'Property check limit reached',
    unit: 'checks',
    cta: "Every check you've run is saved and stays yours. This limit only holds new checks.",
    slots: false,
  },
  question: {
    title: 'Question limit reached',
    unit: 'questions',
    cta: 'Research, strategy and your portfolio all keep working in the meantime.',
    slots: false,
  },
  active_deals: {
    title: 'Purchase slots full',
    unit: 'active purchases',
    cta: 'Close, cancel or settle a purchase to open this one — closed purchases free their slot instantly.',
    slots: true,
  },
  watched_suburbs: {
    title: 'Watched suburbs full',
    unit: 'suburbs',
    cta: 'Remove a watched suburb to add this one — removing frees the slot instantly.',
    slots: true,
  },
  document_analysis: {
    title: 'Document analysis limit reached',
    unit: 'documents',
    cta: 'Documents already analyzed stay in your files. This limit only holds new analyses.',
    slots: false,
  },
  // Legacy aliases (pre-cutover 429 payloads) — API keys, never rendered.
  dd: { alias: 'property_check' },
  deals: { alias: 'active_deals' }, // dsl-allow: legacy API resource key
  chat: { alias: 'question' },
};

// signup_signals hold (backend AllowanceExceeded.hold_reason): the account's
// free allowance is paused at zero, so a usage bar would read a meaningless
// 0 / 0. Each line names the one fact we actually hold. Upgrading lifts it.
const HOLD_COPY = {
  shared_device: 'This browser was already used to open another Heyward account, so this account\'s free allowance is paused while we review it.',
  shared_network: 'Another new Heyward account was opened from the same internet connection in the last day, so this account\'s free allowance is paused while we review it.',
  datacenter_ip: 'This account was opened from a cloud-server network rather than a home or mobile connection, so its free allowance is paused while we review it.',
};
const HOLD_FOOTNOTE = 'If this is a mistake, tell us through the Feedback tab and we\'ll lift it.';

function resolveCopy(resource) {
  const entry = RESOURCE_COPY[resource] || RESOURCE_COPY.property_check;
  return entry.alias ? RESOURCE_COPY[entry.alias] : entry;
}

function formatResets(resets) {
  if (!resets) return null;
  try {
    const d = new Date(resets);
    return d.toLocaleString('en-AU', {
      timeZone: 'Australia/Sydney',
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch {
    return null;
  }
}

export default function QuotaExceededModal({ resource, usage, onClose }) {
  const navigate = useNavigate();
  const hold = HOLD_COPY[usage?.hold_reason] || null;
  const copy = hold
    ? { ...resolveCopy(resource), title: 'Free allowance paused', cta: hold }
    : resolveCopy(resource);
  const used = usage?.used ?? 0;
  const limit = usage?.limit ?? 0;
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 100;
  const resetsStr = formatResets(usage?.resets);

  // Variant (ADR-025 §2.2): a bigger plan unblocks it → upgrade moment;
  // a slot cap → close-one-to-open-one; otherwise it resets on a clock.
  // The upgrade moment is the product's paywall — it names the specific
  // unlock and price (both server-truthful: upgrade_limit comes from the
  // backend LIMITS table, price from the ADR-locked plans config), styled
  // as a boundary rather than an error. Red framing stays for the
  // variants where nothing can be done but wait or free a slot.
  const upId = usage?.upgrade_to || null;
  const upgradeable = Boolean(upId);
  const upLabel = upId ? planLabel(upId) : null;
  const upPrice = (upId && PLANS[upId]?.priceMonthly) || null;
  const upLimit = usage?.upgrade_limit ?? null;
  const primaryLabel = upgradeable ? `Upgrade to ${upLabel} →` : 'View usage →';

  // Paywall funnel telemetry: one `quota_modal_shown` per appearance (the
  // provider mounts a fresh modal per 429), then which button ended it.
  // `plan` is the server's effective plan from the 429 detail — a founder at
  // cap reports plan='founder' with no upgrade target, which is exactly the
  // case this event exists to make visible.
  const funnelProps = { resource, plan: usage?.plan || null, upgradeTo: upId };
  const shownRef = useRef(false); // once per mount, even under StrictMode's dev double-run
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    track.quotaModalShown({ ...funnelProps, used, limit });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const closeVia = (cta) => {
    track.quotaModalCtaClicked(cta, funnelProps);
    onClose();
  };

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.4)' }}
        onClick={() => closeVia('close')}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-sm rounded-[10px] overflow-hidden"
          style={{ background: c.surface, border: `1px solid ${c.border}`, boxShadow: '0 12px 40px rgba(0,0,0,0.14)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="flex items-start justify-between p-5 pb-4"
            style={{ borderBottom: `1px solid ${c.borderF}` }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                style={
                  upgradeable
                    ? { background: c.pageBg, border: `1px solid ${c.border}` }
                    : { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.18)' }
                }
              >
                {upgradeable ? (
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-block',
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      background: 'conic-gradient(from 180deg at 50% 50%, #f43f5e, #6366f1, #f43f5e)',
                    }}
                  />
                ) : (
                  <AlertTriangle size={16} style={{ color: '#ef4444' }} aria-hidden="true" />
                )}
              </div>
              <div>
                <h2
                  className="text-[14px] font-bold leading-tight"
                  style={{ color: c.text, letterSpacing: '-0.01em' }}
                >
                  {copy.title}
                </h2>
              </div>
            </div>
            <button
              onClick={() => closeVia('close')}
              className="p-1.5 rounded-md transition-colors hover:bg-[rgba(0,0,0,0.06)]"
              style={{ color: c.textMute }}
              aria-label="Close"
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>

          {/* Body */}
          <div className="p-5 space-y-4">
            <p className="text-[13.5px] leading-[1.6]" style={{ color: c.textMid }}>
              {copy.cta}
            </p>

            {upgradeable && (
              <div
                className="rounded-md px-3.5 py-3"
                style={{ background: c.pageBg, border: `1px solid ${c.borderF}` }}
              >
                <p className="text-[13px] leading-[1.55]" style={{ color: c.text }}>
                  <span className="font-bold">Heyward {upLabel}</span>
                  {upLimit ? ` raises this to ${upLimit} ${copy.unit}.` : ' lifts this limit.'}
                  {upPrice ? ` ${upPrice} a month.` : ''}
                </p>
              </div>
            )}

            {hold && (
              <p className="text-[12px] leading-[1.55]" style={{ color: c.textFaint }}>
                {HOLD_FOOTNOTE}
              </p>
            )}

            {/* Usage bar */}
            {!hold && <div>
              <div className="flex items-center justify-between text-[11px] mb-1.5">
                <span style={{ color: c.textMute, letterSpacing: '0.06em', fontWeight: 700, textTransform: 'uppercase' }}>
                  Usage
                </span>
                <span className="font-bold tabular-nums" style={{ color: upgradeable ? c.text : '#ef4444' }}>
                  {used} / {limit} {copy.unit}
                </span>
              </div>
              <div
                className="h-2 rounded-full overflow-hidden"
                style={{ background: c.pageBg, border: `1px solid ${c.borderF}` }}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${pct}%`,
                    background: upgradeable
                      ? 'linear-gradient(90deg, #f43f5e, #6366f1)'
                      : 'linear-gradient(90deg, #f43f5e, #ef4444)',
                  }}
                />
              </div>
            </div>}

            {!hold && resetsStr && (
              <p className="text-[12px]" style={{ color: c.textFaint }}>
                Resets: <span style={{ color: c.textMid }}>{resetsStr} AEST</span>
              </p>
            )}
          </div>

          {/* Actions */}
          <div
            className="flex items-center gap-2 px-5 pb-5"
          >
            <button
              onClick={() => { closeVia(upgradeable ? 'upgrade' : 'view_usage'); navigate('/settings?tab=usage'); }}
              className="flex-1 px-4 py-2.5 rounded-md text-[13px] font-semibold text-center transition-colors"
              style={{ background: c.stone800, color: c.surface }}
            >
              {primaryLabel}
            </button>
            <button
              onClick={() => closeVia('dismiss')}
              className="px-4 py-2.5 rounded-md text-[13px] font-semibold transition-colors hover:bg-[rgba(0,0,0,0.04)]"
              style={{ color: c.textMid, border: `1px solid ${c.border}` }}
            >
              Dismiss
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
